import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, Crown, Users, Download, Coins } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { rp, num, fmtDate, downloadCSV, METHODS } from "../lib/format";
import { tierOf } from "../lib/loyalty";
import { useAuth } from "../context/AuthContext";
import { PageWrap, Empty, StatCard } from "../components/common";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";

const EMPTY = { name: "", phone: "", email: "", address: "", birthday: "", note: "" };

const Tier = ({ spent }) => {
  const [t, c] = tierOf(spent || 0);
  return <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full text-white" style={{ background: c }}><Crown className="h-3 w-3" />{t}</span>;
};

const CustomerForm = ({ form, setForm, onSaved }) => {
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    try {
      const body = Object.fromEntries(Object.keys(EMPTY).map((k) => [k, form[k] || ""]));
      if (form.id) await api.put(`/customers/${form.id}`, body);
      else await api.post("/customers", body);
      toast.success("Pelanggan disimpan");
      setForm(null);
      onSaved();
    } catch (e2) {
      toast.error(errMsg(e2));
    }
  };
  return (
    <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
      <DialogContent className="max-w-md" data-testid="customer-form-dialog">
        <DialogHeader><DialogTitle className="font-display">{form?.id ? "Edit" : "Tambah"} Pelanggan</DialogTitle><DialogDescription>Data member untuk program poin.</DialogDescription></DialogHeader>
        {form && (
          <form onSubmit={save} className="grid grid-cols-2 gap-3">
            <div className="col-span-2 space-y-1.5"><Label>Nama</Label><Input required value={form.name} onChange={set("name")} data-testid="customer-name-input" /></div>
            <div className="space-y-1.5"><Label>No. HP</Label><Input inputMode="tel" value={form.phone} onChange={set("phone")} data-testid="customer-phone-input" /></div>
            <div className="space-y-1.5"><Label>Tanggal Lahir</Label><Input type="date" value={form.birthday} onChange={set("birthday")} data-testid="customer-birthday-input" /></div>
            <div className="col-span-2 space-y-1.5"><Label>Email</Label><Input type="email" value={form.email} onChange={set("email")} data-testid="customer-email-input" /></div>
            <div className="col-span-2 space-y-1.5"><Label>Alamat</Label><Textarea rows={2} value={form.address} onChange={set("address")} data-testid="customer-address-input" /></div>
            <div className="col-span-2 space-y-1.5"><Label>Catatan</Label><Input value={form.note} onChange={set("note")} data-testid="customer-note-input" /></div>
            <Button type="submit" className="col-span-2" data-testid="customer-save-btn">Simpan</Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

const CustomerDetail = ({ id, onClose, onChanged }) => {
  const { can } = useAuth();
  const [c, setC] = useState(null);
  const [adj, setAdj] = useState("");
  const load = useCallback(() => id && api.get(`/customers/${id}`).then((r) => setC(r.data)), [id]);
  useEffect(() => { setC(null); load(); }, [load]);
  const adjust = async (sign) => {
    try {
      await api.post(`/customers/${id}/points`, { points: sign * (parseInt(adj, 10) || 0) });
      setAdj("");
      load();
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[95vh] overflow-y-auto" data-testid="customer-detail-dialog">
        <DialogHeader><DialogTitle className="font-display">{c?.name}</DialogTitle><DialogDescription>{c?.code} · {c?.phone}</DialogDescription></DialogHeader>
        {c && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-xl bg-muted/60 p-3"><div className="text-xs text-muted-foreground">Poin</div><div className="font-mono font-bold text-primary" data-testid="customer-detail-points">{num(c.points)}</div></div>
              <div className="rounded-xl bg-muted/60 p-3"><div className="text-xs text-muted-foreground">Total Belanja</div><div className="font-mono font-bold text-sm">{rp(c.total_spent)}</div></div>
              <div className="rounded-xl bg-muted/60 p-3"><div className="text-xs text-muted-foreground">Kunjungan</div><div className="font-mono font-bold">{c.visits}</div></div>
            </div>
            {can("customers") && (
              <div className="flex gap-2 items-center">
                <Input inputMode="numeric" placeholder="Jumlah poin" value={adj} onChange={(e) => setAdj(e.target.value.replace(/\D/g, ""))} data-testid="points-adjust-input" />
                <Button variant="outline" onClick={() => adjust(1)} disabled={!adj} data-testid="points-add-btn">+ Tambah</Button>
                <Button variant="outline" onClick={() => adjust(-1)} disabled={!adj} data-testid="points-sub-btn">- Kurangi</Button>
              </div>
            )}
            <div>
              <h3 className="font-semibold text-sm mb-2">Riwayat Belanja</h3>
              <div className="divide-y rounded-xl border">
                {c.transactions.map((t) => (
                  <div key={t.id} className="flex justify-between px-3 py-2 text-sm">
                    <div><div className="font-mono font-semibold">{t.invoice}</div><div className="text-xs text-muted-foreground">{fmtDate(t.created_at)} · {METHODS[t.payment_method]}</div></div>
                    <div className="text-right"><div className={`font-mono font-bold ${t.status === "batal" ? "line-through text-muted-foreground" : ""}`}>{rp(t.total)}</div>{t.points_earned > 0 && <div className="text-xs text-success">+{t.points_earned} poin</div>}</div>
                  </div>
                ))}
                {!c.transactions.length && <p className="text-sm text-muted-foreground p-3">Belum ada transaksi.</p>}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default function Customers() {
  const { can, store } = useAuth();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [form, setForm] = useState(null);
  const [detail, setDetail] = useState(null);
  const [del, setDel] = useState(null);
  const load = useCallback(() => api.get("/customers", { params: { q } }).then((r) => setRows(r.data)), [q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const remove = async () => {
    try { await api.delete(`/customers/${del.id}`); toast.success("Pelanggan dihapus"); load(); } catch (e) { toast.error(errMsg(e)); }
    setDel(null);
  };
  const exportCsv = () => downloadCSV("pelanggan.csv", [["Kode", "Nama", "HP", "Email", "Poin", "Total Belanja", "Kunjungan"], ...rows.map((c) => [c.code, c.name, c.phone, c.email, c.points, c.total_spent, c.visits])]);
  return (
    <PageWrap>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard testid="customers-total" icon={Users} label="Total Member" value={rows.length} />
        <StatCard testid="customers-points" icon={Coins} tone="warning" label="Poin Beredar" value={num(rows.reduce((a, b) => a + (b.points || 0), 0))} hint={store?.loyalty_enabled ? `1 poin per ${rp(store.points_per_amount)} · 1 poin = ${rp(store.point_value)}` : "Program poin belum aktif (Pengaturan → Member & Notifikasi)"} />
        <StatCard testid="customers-spent" icon={Crown} tone="success" label="Belanja Member" value={rp(rows.reduce((a, b) => a + (b.total_spent || 0), 0))} />
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9" placeholder="Cari nama, no. HP, kode member..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="customers-search-input" />
        </div>
        <Button variant="outline" onClick={exportCsv} data-testid="customers-export-btn"><Download className="h-4 w-4 mr-2" />CSV</Button>
        <Button onClick={() => setForm({ ...EMPTY })} data-testid="add-customer-btn"><Plus className="h-4 w-4 mr-2" />Tambah Pelanggan</Button>
      </div>
      <div className="rounded-2xl border bg-card overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Pelanggan</TableHead><TableHead>Level</TableHead><TableHead className="text-right">Poin</TableHead><TableHead className="text-right hidden md:table-cell">Total Belanja</TableHead><TableHead className="hidden lg:table-cell">Terakhir</TableHead><TableHead className="text-right">Aksi</TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.map((c) => (
              <TableRow key={c.id} className="cursor-pointer" onClick={() => setDetail(c.id)} data-testid={`customer-row-${c.id}`}>
                <TableCell><div className="font-semibold">{c.name}</div><div className="text-xs text-muted-foreground font-mono">{c.code} · {c.phone || "-"}</div></TableCell>
                <TableCell><Tier spent={c.total_spent} /></TableCell>
                <TableCell className="text-right font-mono font-bold text-primary">{num(c.points)}</TableCell>
                <TableCell className="text-right font-mono hidden md:table-cell">{rp(c.total_spent)}</TableCell>
                <TableCell className="text-sm hidden lg:table-cell">{c.last_visit ? fmtDate(c.last_visit) : "-"}</TableCell>
                <TableCell className="text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                  {can("customers") && (<>
                    <Button size="icon" variant="ghost" onClick={() => setForm(c)} data-testid={`edit-customer-${c.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="text-destructive" onClick={() => setDel(c)} data-testid={`delete-customer-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </>)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {!rows.length && <Empty icon={Users} text="Belum ada pelanggan." />}
      </div>
      <CustomerForm form={form} setForm={setForm} onSaved={load} />
      <CustomerDetail id={detail} onClose={() => setDetail(null)} onChanged={load} />
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} title="Hapus pelanggan?" description={`"${del?.name}" beserta poinnya akan dihapus.`} onConfirm={remove} />
    </PageWrap>
  );
}
